import { useState } from "react";
import { Mail, Phone, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, solvePow } from "../../lib/api";

export function RevealContact({ target = "studio", kind, compact }) {
  const [value, setValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const Icon = kind === "email" ? Mail : Phone;
  const label = kind === "email" ? "Mostra email" : "Mostra telefono";
  const tid = `reveal-${kind}-${target === "studio" ? "studio" : "team"}`;

  const reveal = async () => {
    setBusy(true); setErr("");
    try {
      const pow = await solvePow();
      const { data } = await api.post("/public/reveal", { target, kind, ...pow });
      setValue(data.value);
    } catch (e) { setErr(errMsg(e, "Non è stato possibile mostrare il recapito.")); }
    setBusy(false);
  };
  const copy = async () => { await navigator.clipboard?.writeText(value); toast.success("Copiato negli appunti"); };

  if (!value) return (
    <div>
      <button onClick={reveal} disabled={busy} className={`tap inline-flex items-center gap-2 rounded-md border border-primary/30 px-4 text-sm font-medium hover:bg-secondary transition-colors ${compact ? "py-1" : ""}`} data-testid={tid}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Icon className="h-4 w-4" aria-hidden />}{busy ? "Verifica in corso…" : label}
      </button>
      {err && <p role="alert" className="text-sm text-destructive mt-2">{err}</p>}
    </div>
  );
  const href = kind === "email" ? `mailto:${value}` : `tel:${value.replace(/\s/g, "")}`;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-live="polite" data-testid={`${tid}-value`}>
      <a href={href} className="tap inline-flex items-center gap-2 font-semibold text-primary underline underline-offset-4"><Icon className="h-4 w-4" aria-hidden />{value}</a>
      <button onClick={copy} className="tap min-w-[44px] grid place-items-center rounded-md border" aria-label="Copia recapito" data-testid={`${tid}-copy`}><Copy className="h-4 w-4" /></button>
    </div>
  );
}

export function Monogram({ name, photo, size = "lg" }) {
  const initials = name.split(" ").filter((w) => /^[A-Z]/.test(w)).slice(-2).map((w) => w[0]).join("");
  const s = size === "lg" ? "h-24 w-24 text-3xl" : "h-14 w-14 text-xl";
  if (photo) return <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={`Fotografia di ${name}`} className={`${s} object-cover rounded-full`} loading="lazy" />;
  return <div aria-hidden className={`${s} rounded-full grid place-items-center bg-primary text-primary-foreground font-display font-semibold ring-1 ring-offset-4 ring-[hsl(var(--accent))]`}>{initials}</div>;
}

export function TeamCard({ t }) {
  const full = `${t.title ? t.title + " " : ""}${t.name}`;
  return (
    <article className="card-line p-6 sm:p-8 flex flex-col gap-5 h-full fade-up" data-testid={`team-card-${t.id}`}>
      <Monogram name={t.name} photo={t.photo_url} />
      <div>
        <h3 className="text-xl sm:text-2xl font-semibold break-words">{full}</h3>
        {t.role && <p className="text-sm text-muted-foreground mt-1">{t.role}</p>}
      </div>
      {t.bio && <p className="text-sm leading-relaxed whitespace-pre-line">{t.bio}</p>}
      {[["Competenze", t.skills], ["Ambiti di attività", t.areas], ["Lingue", t.languages]].map(([l, arr]) => arr?.length > 0 && (
        <div key={l}><p className="eyebrow mb-2">{l}</p><ul className="flex flex-wrap gap-2">{arr.map((x) => <li key={x} className="text-xs px-2.5 py-1 rounded bg-secondary">{x}</li>)}</ul></div>
      ))}
      {(t.has_email || t.has_phone) && <div className="mt-auto flex flex-wrap gap-2 pt-2">
        {t.has_email && <RevealContact target={t.id} kind="email" compact />}
        {t.has_phone && <RevealContact target={t.id} kind="phone" compact />}
      </div>}
    </article>
  );
}

export function Field({ id, label, error, hint, children, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium">{label}{required && <span aria-hidden className="text-destructive"> *</span>}</label>
      {children}
      {hint && <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p id={`${id}-err`} role="alert" className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export const inputCls = "w-full tap rounded-md border border-input bg-white px-3 py-2 text-base focus-visible:outline-2";
export const btnPrimary = "tap inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors";
export const btnOutline = "tap inline-flex items-center justify-center gap-2 rounded-md border border-primary/40 px-5 font-semibold text-primary hover:bg-secondary disabled:opacity-60 transition-colors";
export const btnGold = "tap inline-flex items-center justify-center gap-2 rounded-md bg-[hsl(var(--accent))] px-5 font-semibold text-[hsl(var(--accent-foreground))] hover:brightness-95 transition-[filter]";
